import React from 'react';
import {
  Box, Button, Flex, HStack, Icon, Link, Stack, Text,
} from '@chakra-ui/react';
import { getDateString, getTimeFromDate } from '../../utils/dateUtils';

export interface EventAddress {
  name?: string | null; line1?: string | null; line2?: string | null;
  city?: string | null; postCode?: string | null;
}

const Pin = () => (
  <Icon
    viewBox="0 0 24 24"
    boxSize="16px"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    flexShrink={0}
  >
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </Icon>
);
const Clock = () => (
  <Icon
    viewBox="0 0 24 24"
    boxSize="16px"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    flexShrink={0}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Icon>
);
const Cal = () => (
  <Icon
    viewBox="0 0 24 24"
    boxSize="16px"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    flexShrink={0}
  >
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 11h18" />
  </Icon>
);

export const formatVenue = (address?: EventAddress | null): string => [
  address?.name, address?.line1, address?.line2, address?.city, address?.postCode,
].filter(Boolean).join(', ');

/** Google Maps search link for the venue, or null when there is no address. */
export const mapHref = (address?: EventAddress | null): string | null => {
  const venue = formatVenue(address);
  return venue ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}` : null;
};

/**
 * A .ics download, so the date survives leaving the page.
 *
 * Built inline rather than server-side: it is a handful of literal fields and
 * needs no round trip.
 */
export const calendarHref = (name: string, start?: Date | null, end?: Date | null, address?: EventAddress | null): string | null => {
  if (!start) return null;
  const stamp = (d: Date) => `${d.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
    `DTSTART:${stamp(start)}`,
    end ? `DTEND:${stamp(end)}` : '',
    `SUMMARY:${name}`,
    `LOCATION:${formatVenue(address)}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].filter(Boolean);
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join('\n'))}`;
};

interface Props {
  name: string;
  startDateTime?: Date | string | null;
  endDateTime?: Date | string | null;
  address?: EventAddress | null;
  /** Show the calendar / map actions. Off in compact contexts like a card. */
  withActions?: boolean;
  color?: string;
  /** Set on a dark hero so the outline actions stay legible. */
  onDark?: boolean;
  /** Off where the date is already shown, such as above an event's title. */
  showDate?: boolean;
  /** Quiet underlined links instead of outlined buttons, for a hero. */
  actionsAs?: 'button' | 'link';
  fontSize?: string | string[];
}

/**
 * When and where — the two facts that matter most for a one-off event.
 *
 * A course is a recurring commitment, so its listing leads with what it is. An
 * event happens once: if the date and venue are not immediately scannable, the
 * page has buried its most important content.
 */
const EventFacts = ({
  name, startDateTime, endDateTime, address, withActions = false, color, onDark = false,
  showDate = true, actionsAs = 'button', fontSize = '15px',
}: Props) => {
  // Chakra's outline buttons draw a dark border and dark label, which vanish on
  // the indigo hero. On dark, borrow the surface colour instead.
  const actionStyle = onDark
    ? {
      variant: 'outline' as const,
      borderColor: 'whiteAlpha.700',
      color: 'white',
      _hover: { bg: 'whiteAlpha.200' },
      _active: { bg: 'whiteAlpha.300' },
    }
    : { variant: 'outline' as const };
  const linkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    color: onDark ? 'white' : 'blue.900',
    textDecoration: 'underline',
    textDecorationColor: onDark ? 'whiteAlpha.500' : 'blue.300',
    textUnderlineOffset: '5px',
    _hover: { textDecorationColor: 'currentColor' },
  };
  const start = startDateTime ? new Date(startDateTime as string) : null;
  const end = endDateTime ? new Date(endDateTime as string) : null;
  const venue = formatVenue(address);
  const map = mapHref(address);
  const ics = calendarHref(name, start, end, address);

  return (
    <Stack spacing={3} color={color}>
      <Stack spacing={1.5} fontSize={fontSize}>
        {showDate && start && (
          <HStack spacing={2.5} align="center">
            <Cal />
            <Text>
              {getDateString(start)}
              {end ? `, ${getTimeFromDate(start)} – ${getTimeFromDate(end)}` : ''}
            </Text>
          </HStack>
        )}
        {showDate && !end && start && (
          <HStack spacing={2.5} align="center">
            <Clock />
            <Text>{getTimeFromDate(start)}</Text>
          </HStack>
        )}
        {venue && (
          <HStack spacing={2.5} align="flex-start">
            <Box pt="0.2em"><Pin /></Box>
            <Text>{venue}</Text>
          </HStack>
        )}
      </Stack>

      {withActions && actionsAs === 'link' && (
        <Flex gap={6} wrap="wrap" pt={3} fontSize="15px">
          {ics && (
            <Link href={ics} download={`${name}.ics`} {...linkStyle}>
              <Cal />
              Add to calendar
            </Link>
          )}
          {map && (
            <Link href={map} isExternal {...linkStyle}>
              <Pin />
              View on map
            </Link>
          )}
        </Flex>
      )}

      {withActions && actionsAs === 'button' && (
        <Flex gap={2} wrap="wrap" pt={1}>
          {ics && (
            <Button as="a" href={ics} download={`${name}.ics`} size="sm" leftIcon={<Cal />} {...actionStyle}>
              Add to calendar
            </Button>
          )}
          {map && (
            <Button
              as="a"
              href={map}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              leftIcon={<Pin />}
              {...actionStyle}
            >
              View on map
            </Button>
          )}
        </Flex>
      )}
    </Stack>
  );
};

export default EventFacts;
